import React, { useState, useEffect } from 'react';
import { Box, CssBaseline, useMediaQuery, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import Toolbar from './Toolbar';
import Sidebar from './Sidebar';
import Footer from './Footer';
import DynamicBreadcrumbs from './DynamicBreadcrumbs';
import ReleaseNotesPopup from '../../pages/release-notes/ReleaseNotesPopup';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get current route location
  const location = useLocation();
  // Convert pathname to a class-friendly string, e.g. '/dashboard/list' => 'page-dashboard-list'
  const pageClassName =
    'page-' +
      location.pathname
        .replace(/\/+$/, '') // remove trailing slash
        .replace(/^\//, '') // remove leading slash
        .replace(/\//g, '-') // replace slashes with dashes
        .replace(/[^a-zA-Z0-9-_]/g, '') // remove invalid chars
        .toLowerCase() || 'page-root';

  // Initialize drawer state based on screen size
  const [open, setOpen] = useState(!isMobile);
  const [drawerWidth, setDrawerWidth] = useState(178);

  // Update drawer state when screen size changes
  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isMobile]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />

      <Toolbar handleDrawerToggle={handleDrawerToggle} />
      <ReleaseNotesPopup />

      <Sidebar
        open={open}
        handleDrawerToggle={handleDrawerToggle}
        drawerWidth={drawerWidth}
        setDrawerWidth={setDrawerWidth}
      />

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          width: `calc(100% - ${open ? drawerWidth : 0}px)`,
          // marginLeft: open ? `${drawerWidth}px` : '0',
          height: '100%',
          pt: '72px', // Account for fixed toolbar height
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Box
          className={pageClassName}
          sx={{
            flexGrow: 1,
            p: {
              xs: 2,
              sm: 3,
              md: 0,
              lg: 0,
              xl: 0,
            },
            pb: 8,
          }}
        >
          <DynamicBreadcrumbs />
          {children}
        </Box>

        {/* Footer */}
        <Footer />
      </Box>
    </Box>
  );
};

export default Layout;
